import React, { useEffect, useRef } from 'react';
import './Common.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
}

/**
 * Modal — ISS-023 대응.
 *
 * 닫힘 트리거:
 *  1) ESC 키 누름
 *  2) X 버튼 / 취소 버튼 클릭
 *  3) 오버레이 "클릭" (mousedown 과 mouseup 시작·종료점이 모두 오버레이일 때만)
 *
 * 모달 내부 input/텍스트를 드래그하다 마우스가 오버레이 밖까지 끌려 나간 경우
 * mousedown 시작점이 오버레이가 아니므로 onClose 가 호출되지 않는다.
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  onConfirm,
  confirmText = '확인',
  cancelText = '취소',
}) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const mouseDownOnOverlayRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    mouseDownOnOverlayRef.current = e.target === overlayRef.current;
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    const startedOnOverlay = mouseDownOnOverlayRef.current;
    const endedOnOverlay = e.target === overlayRef.current;
    mouseDownOnOverlayRef.current = false;
    if (startedOnOverlay && endedOnOverlay) {
      onClose();
    }
  };

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3 className="modal__title">{title}</h3>
          <button className="modal__close" onClick={onClose}>✕</button>
        </div>
        <div className="modal__body">{children}</div>
        <div className="modal__footer">
          <button className="btn btn--secondary" onClick={onClose}>{cancelText}</button>
          {onConfirm && <button className="btn btn--primary" onClick={onConfirm}>{confirmText}</button>}
        </div>
      </div>
    </div>
  );
};

export default Modal;
